// mobile-catalog — lógica PURA das abas de catálogo (Filmes/Séries/Ao vivo/
// Minha lista) e da tela de detalhe. Parede A5.
//
// Mesmo padrão de mobile-shell-view.ts / mobile-player-seek.ts: funções puras,
// testáveis sem DOM. A apresentação (grids/chips/detalhe touch) só LIGA estas
// funções; nada de React/Norigin aqui. Reúsam-se também na detecção de
// no_provider (idêntica à Home A3) pra as abas mostrarem o mesmo CTA, não crash.

/** Chip de categoria/bucket renderizado no topo das abas. `id` null = "Todos". */
export interface CatalogChip {
  /** id da categoria/bucket, ou null pro chip "Todos". */
  id: string | null
  label: string
}

/** Categoria genérica (LiveCategory tem id+name; buckets têm id+label). */
interface NamedCategory {
  id: string
  name?: string | undefined
  label?: string | undefined
  count?: number | undefined
}

/**
 * Monta a fila de chips: "Todos" (id null) na frente + categorias na ordem
 * recebida. Categorias sem nome/label são puladas (defensivo contra dados
 * sujos do Xtream). `allLabel` é o texto já traduzido do chip "Todos".
 */
export function buildCatalogChips(
  categories: ReadonlyArray<NamedCategory>,
  allLabel: string,
): CatalogChip[] {
  const chips: CatalogChip[] = [{ id: null, label: allLabel }]
  for (const c of categories) {
    const label = (c.label ?? c.name ?? '').trim()
    if (!c.id || !label) continue
    chips.push({ id: c.id, label })
  }
  return chips
}

/** True quando o chip está selecionado (compara id, tratando null = "Todos"). */
export function isChipActive(chipId: string | null, activeId: string | null): boolean {
  return chipId === activeId
}

/** Episódio achatado da série (todas as temporadas em ordem) pronto pra lista
 *  e pra abrir o player (signStream usa o episodeId; salvar posição usa
 *  series/season/episode). */
export interface FlatEpisode {
  episodeId: string
  seasonKey: string
  seasonNumber: number
  episodeNumber: number
  title: string
  poster: string | null
}

/** Formato cru de uma temporada vindo do getSeriesInfo (Xtream/TMDB). */
interface RawEpisode {
  id: string
  episode_num?: string | undefined
  title?: string | undefined
  season?: number | undefined
  info?:
    | {
        movie_image?: string | null | undefined
        cover_big?: string | null | undefined
      }
    | undefined
}

/**
 * Achata o mapa seasons (Record<seasonKey, RawEpisode[]>) numa lista ordenada
 * por temporada e por episódio. Usado pra:
 *  - renderizar a lista de episódios da temporada ativa;
 *  - resolver season/episode ao abrir o player (salvar posição de série, que
 *    ficou pendente na A4 por faltar season/ep).
 *
 * `seasonKey` é a chave do mapa (string numérica "1","2",...). episodeNumber
 * vem de episode_num quando válido, senão da posição (i+1). Episódios sem id
 * são pulados (não dá pra assinar stream sem id).
 */
export function flattenSeasons(
  seasons: Record<string, ReadonlyArray<RawEpisode>> | undefined | null,
): FlatEpisode[] {
  if (!seasons) return []
  const out: FlatEpisode[] = []
  const keys = Object.keys(seasons).sort((a, b) => Number(a) - Number(b))
  for (const k of keys) {
    const eps = seasons[k] ?? []
    eps.forEach((ep, i) => {
      if (!ep.id) return
      const episodeNumber = Number(ep.episode_num) || i + 1
      const seasonNumber = Number(k) || ep.season || 0
      out.push({
        episodeId: ep.id,
        seasonKey: k,
        seasonNumber,
        episodeNumber,
        title: (ep.title ?? '').trim() || `${episodeNumber}`,
        poster: ep.info?.movie_image ?? ep.info?.cover_big ?? null,
      })
    })
  }
  return out
}

/** Chaves de temporada ordenadas numericamente. */
export function seasonKeysOf(
  seasons: Record<string, unknown> | undefined | null,
): string[] {
  if (!seasons) return []
  return Object.keys(seasons).sort((a, b) => Number(a) - Number(b))
}

// ===== Alvos do player (puros) — montados pela tela de detalhe ao tocar
// "Assistir" (VOD) ou um episódio (série). Separados aqui pra serem testáveis
// sem DOM: a parte "episódio → alvo com season/ep" é o que destrava salvar a
// posição de série (pendente da A4), então merece teste de função pura. =====

/** Alvo do player pra um filme (VOD). `id` é o vodId (signStream usa ele). */
export interface VodPlayTarget {
  id: string
  kind: 'vod'
  title?: string | undefined
  poster?: string | null | undefined
  resumeFrom?: number | undefined
}

/** Alvo do player pra um EPISÓDIO de série. `id` é o episodeId (signStream
 *  usa ele); os campos series* carregam o contexto pra salvar a posição.
 *  resumeFrom retoma o episódio de onde parou (continuar de série). */
export interface EpisodePlayTarget {
  id: string
  kind: 'series'
  title?: string | undefined
  poster?: string | null | undefined
  seriesId: string
  episodeId: string
  seasonNumber: number
  episodeNumber: number
  resumeFrom?: number | undefined
}

/** Monta o alvo do player pra um filme. resumeFrom só entra quando > 0. */
export function buildVodTarget(
  id: string,
  meta: { title?: string | undefined; poster?: string | null | undefined; resumeFrom?: number },
): VodPlayTarget {
  const resume = meta.resumeFrom ?? 0
  return {
    id,
    kind: 'vod',
    title: meta.title,
    poster: meta.poster,
    ...(resume > 0 ? { resumeFrom: resume } : {}),
  }
}

/** Monta o alvo do player pra um episódio. O título ganha o sufixo T·E e o
 *  poster cai pro da série quando o episódio não tem thumb própria. resumeFrom
 *  (continuar de série) só entra quando > 0. */
export function buildEpisodeTarget(
  ep: FlatEpisode,
  ctx: {
    seriesId: string
    seriesTitle?: string | undefined
    seriesPoster?: string | null | undefined
    resumeFrom?: number | undefined
  },
): EpisodePlayTarget {
  const base = (ctx.seriesTitle ?? '').trim()
  const suffix = `T${ep.seasonNumber} E${ep.episodeNumber}`
  const resume = ctx.resumeFrom ?? 0
  return {
    id: ep.episodeId, // signStream('series', episodeId)
    kind: 'series',
    title: base ? `${base} · ${suffix}` : suffix,
    poster: ep.poster ?? ctx.seriesPoster ?? null,
    seriesId: ctx.seriesId,
    episodeId: ep.episodeId,
    seasonNumber: ep.seasonNumber,
    episodeNumber: ep.episodeNumber,
    ...(resume > 0 ? { resumeFrom: resume } : {}),
  }
}

/** Progresso salvo de um episódio (subconjunto do PlaybackPosition do local-db,
 *  só o que a escolha do "continuar" precisa). Mantido aqui pra a função pura
 *  não depender do módulo de IndexedDB. */
export interface EpisodeProgress {
  episodeId: string
  positionSec: number
  durationSec: number
  lastWatchedAt: number
  finished?: boolean | undefined
}

/** Resultado da escolha do "continuar" de série. mode='resume' retoma o ep em
 *  andamento (de positionSec); mode='next' começa o próximo (positionSec=0);
 *  mode='start' não há progresso, começa o 1º episódio. */
export interface SeriesContinue {
  episode: FlatEpisode
  mode: 'resume' | 'next' | 'start'
  positionSec: number
}

/**
 * Decide qual episódio o botão principal "Assistir/Continuar" deve abrir, dado
 * o achatamento de episódios (ordem cronológica) e o progresso salvo por
 * episodeId. Replica a regra da SeriesDetailsScreen da TV (nextToWatch):
 *
 *   1) Sem progresso → 1º episódio do catálogo (mode='start').
 *   2) Episódio mais recente (maior lastWatchedAt) em andamento → retoma esse
 *      (mode='resume'). NÃO usa "primeiro ep não-finished" (bug histórico: ver
 *      ep removido do catálogo ou ep antigo abandonado).
 *   3) Mais recente válido finalizado → próximo na ordem cronológica
 *      (mode='next'). Foi o último → cai no 1º (mode='start'), pra "reassistir".
 *   4) Episódios salvos que sumiram do catálogo (provider volátil) são pulados.
 *
 * `flat` vazio → null (sem nada pra tocar).
 */
export function pickSeriesContinue(
  flat: ReadonlyArray<FlatEpisode>,
  progressByEpisodeId: Readonly<Record<string, EpisodeProgress>>,
): SeriesContinue | null {
  const first = flat[0]
  if (!first) return null

  const sorted = Object.values(progressByEpisodeId)
    .filter((p) => p.episodeId)
    .sort((a, b) => b.lastWatchedAt - a.lastWatchedAt)

  for (const pos of sorted) {
    const idx = flat.findIndex((e) => e.episodeId === pos.episodeId)
    if (idx < 0) continue // ep saiu do catálogo, tenta o próximo progresso

    if (!pos.finished) {
      return { episode: flat[idx]!, mode: 'resume', positionSec: Math.max(0, pos.positionSec) }
    }
    // Mais recente válido foi finalizado → próximo na ordem cronológica.
    const next = flat[idx + 1]
    if (next) return { episode: next, mode: 'next', positionSec: 0 }
    // Foi o último → reassistir do 1º.
    return { episode: first, mode: 'start', positionSec: 0 }
  }

  // Nada salvo (ou só eps que sumiram do catálogo) → 1º episódio.
  return { episode: first, mode: 'start', positionSec: 0 }
}
